import { Block as BlockIcon, Lock as LockIcon } from 'components/icons';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import { FormattedDate } from 'components/misc/FormattedDate';
import {
    AddNewCreditInvoiceForm,
    InputScopeType,
    MatterAmountForm,
} from 'components/modals/invoices/AddNewCreditInvoiceFormModal';
import type { DataTableColumnDef } from 'components/tables/data-table';
import MoneyDisplay from 'components/util/MoneyDisplay';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo } from 'react';
import { Control, Path, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { MatterListItem, MatterStateCountDto, MatterStateType } from 'types/matters';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { hasLimitedVisibility, isConfidentialMatter } from 'utils/matterOptionRenderer';
import { MatterAmountAccordionTitle } from './MatterAmountAccordionTitle';
import MattersAccordionTable from './MattersAccordionTable';

interface Props {
    control: Control<AddNewCreditInvoiceForm>;
    setValue: UseFormSetValue<AddNewCreditInvoiceForm>;
    getValues: UseFormGetValues<AddNewCreditInvoiceForm>;
    selectedInputScopes: InputScopeType[];
    setSelectedInputScopes: (value: InputScopeType[]) => void;
    classifiers: Record<string, ClassifierData>;
    matterStateCount: MatterStateCountDto | undefined;
    passiveFilter: string;
    searchText: string;
    rowsPerPage?: number;
    disableUrlState?: boolean;
}

type MatterAmountGroup = 'inProgress' | 'pending' | 'completed' | 'offerWon' | 'offerFailed' | 'archived';

/** The row's amount input, revealed while the row's own checkbox is ticked. Its own component
 *  because it subscribes to that checkbox through `useWatch`. */
const AmountCell = ({
    control,
    fieldName,
    rowIndex,
}: {
    control: Control<AddNewCreditInvoiceForm>;
    fieldName: ReturnType<typeof getFieldName>;
    rowIndex: number;
}) => {
    const isSelected = useWatch({
        control,
        // RHF Path<T> can't be narrowed from a template-string path; bridge cast
        // (not an unknown downcast — the runtime shape is valid).
        name: `${fieldName}.${rowIndex}.selected` as Path<AddNewCreditInvoiceForm>,
    });
    return (
        <DecimalInput
            gap={0}
            name={`${fieldName}.${rowIndex}.amount`}
            control={control}
            placeholder="invoices.new-credit-note-modal.add-amount"
            required={!!isSelected}
            wrapperClassName={isSelected ? undefined : 'hidden'}
        />
    );
};

const getFieldName = (matterStateTypeCode: string | undefined): MatterAmountGroup => {
    switch (matterStateTypeCode) {
        case MatterStateType.COMPLETED:
            return 'completed';
        case MatterStateType.IN_PROGRESS:
            return 'inProgress';
        case MatterStateType.OFFER_WON:
            return 'offerWon';
        case MatterStateType.OFFER_LOST:
            return 'offerFailed';
        case MatterStateType.OFFER_PENDING:
            return 'pending';
        default:
            return 'archived';
    }
};

export const MatterAmountFormTable = (props: Props): JSX.Element => {
    const {
        control,
        setValue,
        getValues,
        selectedInputScopes,
        setSelectedInputScopes,
        classifiers,
        matterStateCount,
        passiveFilter,
        searchText,
        rowsPerPage = 10,
        disableUrlState = false,
    } = props;
    const [inProgress, completed, pending, offerWon, offerFailed] = useWatch({
        control,
        name: ['inProgress', 'completed', 'pending', 'offerWon', 'offerFailed'],
    });

    const stateCount = useMemo(
        () => matterStateCount?.matterGroups.find((item) => item.groupName === 'connected')?.stateCount,
        [matterStateCount]
    );

    const hasMoreMattersInDefinedScope = useCallback(
        (definedScope: InputScopeType, matterId: number) =>
            [
                ...getValues('inProgress'),
                ...getValues('pending'),
                ...getValues('completed'),
                ...getValues('offerWon'),
                ...getValues('offerFailed'),
            ].some(
                (item) =>
                    item.selected &&
                    item.currency === definedScope.currency &&
                    item.officeId === definedScope.officeId &&
                    item.payerId === definedScope.payerId &&
                    item.id !== matterId
            ),
        [getValues]
    );

    const columns: DataTableColumnDef<MatterListItem>[] = useMemo(
        () => [
            {
                header: t('invoices.new-credit-note-modal.case-name'),
                id: 'selectCase',
                enableSorting: false,
                meta: {
                    // This column takes the ~52% the other three (16% each) leave. Measured in the
                    // 900px modal: 426 / 131 / 131 / 131 px.
                    widthWeight: 3.25,
                    minPx: 260,
                },
                cell: ({ row }) => {
                    const fieldName = getFieldName(row.original.matterStateTypeCode);
                    const currency = row.original.pricelistCurrencyTypeCode;
                    const officeId = row.original.invoicedByOfficeId;
                    const payerId = row.original.payerPartyId;
                    const matterId = row.original.id;

                    const scopeList = cloneDeep(selectedInputScopes);
                    const definedScope = scopeList.find(
                        (item) => item.currency === currency && item.officeId === officeId && item.payerId === payerId
                    );

                    const hasLimitedAccess = hasLimitedVisibility(row.original);
                    const isConfidential = isConfidentialMatter(row.original);

                    return (
                        <div className={cn('flex items-center', hasLimitedAccess && 'bg-grey-200 opacity-70')}>
                            <CheckboxInput
                                name={`${fieldName}.${row.index}.selected`}
                                control={control}
                                disabled={hasLimitedAccess}
                                onChange={(value) => {
                                    if (hasLimitedAccess) return;

                                    setValue(`${fieldName}.${row.index}.selected`, value);
                                    setValue(`${fieldName}.${row.index}.amount`, '');

                                    if (value && !definedScope) {
                                        scopeList.push({ currency, officeId, payerId });
                                        setSelectedInputScopes(scopeList);
                                    } else if (
                                        !value &&
                                        definedScope &&
                                        !hasMoreMattersInDefinedScope(definedScope, matterId)
                                    ) {
                                        const index = scopeList.indexOf(definedScope);
                                        if (index > -1) {
                                            scopeList.splice(index, 1);
                                            setSelectedInputScopes(scopeList);
                                        }
                                    }
                                }}
                            />
                            <div className="flex flex-1 items-center gap-2">
                                {/* CheckboxInput ids its checkbox by the field name; the label names it. */}
                                <label htmlFor={`${fieldName}.${row.index}.selected`} className="flex-1">
                                    {row.original?.fullName ?? ''}
                                </label>
                                {hasLimitedAccess && (
                                    <div className="flex gap-1">
                                        {isConfidential ? (
                                            <LockIcon className="text-grey-600 text-[16px]" />
                                        ) : (
                                            <BlockIcon className="text-grey-600 text-[16px]" />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('invoices.new-credit-note-modal.date'),
                // Functional accessor with no id: the managed layout would otherwise key this
                // column on its TRANSLATED header.
                id: 'openedDate',
                accessorFn: (row) => row.openedDate,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => <FormattedDate value={getValue<MatterListItem['openedDate']>()} fallback="" />,
            },
            {
                header: t('invoices.new-credit-note-modal.case-currency'),
                id: 'currency',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ row }) => getClassifierText(classifiers['CURRENCY'], row.original.pricelistCurrencyTypeCode),
            },
            {
                header: '',
                id: 'amount',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ row }) => (
                    <AmountCell
                        control={control}
                        fieldName={getFieldName(row.original.matterStateTypeCode)}
                        rowIndex={row.index}
                    />
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedInputScopes, control, setValue, setSelectedInputScopes, classifiers]
    );

    const mapMatterData = useCallback(
        (groupName: MatterAmountGroup, data: MatterListItem[]) => {
            const mapped: MatterAmountForm[] = data.map((item) => ({
                id: item.id,
                officeId: item.invoicedByOfficeId,
                payerId: item.payerPartyId,
                currency: item.pricelistCurrencyTypeCode,
                selected: false,
                amount: '',
            }));
            setValue(groupName, mapped);
        },
        [setValue]
    );

    const handleInProgressDataChange = useCallback(
        (data: MatterListItem[]) => mapMatterData('inProgress', data),
        [mapMatterData]
    );
    const handleCompletedDataChange = useCallback(
        (data: MatterListItem[]) => mapMatterData('completed', data),
        [mapMatterData]
    );
    const handlePendingDataChange = useCallback(
        (data: MatterListItem[]) => mapMatterData('pending', data),
        [mapMatterData]
    );
    const handleOfferWonDataChange = useCallback(
        (data: MatterListItem[]) => mapMatterData('offerWon', data),
        [mapMatterData]
    );
    const handleOfferFailedDataChange = useCallback(
        (data: MatterListItem[]) => mapMatterData('offerFailed', data),
        [mapMatterData]
    );

    const dataChangeHandlers: Record<string, (data: MatterListItem[]) => void> = useMemo(
        () => ({
            inProgress: handleInProgressDataChange,
            completed: handleCompletedDataChange,
            pending: handlePendingDataChange,
            offerWon: handleOfferWonDataChange,
            offerFailed: handleOfferFailedDataChange,
        }),
        [
            handleInProgressDataChange,
            handleCompletedDataChange,
            handlePendingDataChange,
            handleOfferWonDataChange,
            handleOfferFailedDataChange,
        ]
    );

    const handleGetPassiveFilter = useCallback(
        (state: string) => {
            switch (state) {
                case MatterStateType.COMPLETED:
                    return `matterStateTypeCode=in=(COMPLETED);${passiveFilter}`;
                case MatterStateType.OFFER_WON:
                    return `matterStateTypeCode=in=(OFFER_WON);${passiveFilter}`;
                case MatterStateType.OFFER_LOST:
                    return `matterStateTypeCode=in=(OFFER_LOST);${passiveFilter}`;
                case MatterStateType.OFFER_PENDING:
                    return `matterStateTypeCode=in=(OFFER_PENDING);${passiveFilter}`;
                case MatterStateType.IN_PROGRESS:
                    return `matterStateTypeCode=in=(IN_PROGRESS);${passiveFilter}`;
                default:
                    return undefined;
            }
        },
        [passiveFilter]
    );

    const getGroupName = (state: string) => {
        let groupName: MatterAmountGroup = 'archived';
        switch (state) {
            case MatterStateType.COMPLETED:
                groupName = 'completed';
                break;
            case MatterStateType.OFFER_WON:
                groupName = 'offerWon';
                break;
            case MatterStateType.OFFER_LOST:
                groupName = 'offerFailed';
                break;
            case MatterStateType.OFFER_PENDING:
                groupName = 'pending';
                break;
            case MatterStateType.IN_PROGRESS:
                groupName = 'inProgress';
                break;
        }
        return groupName;
    };

    return (
        // The header stays a div: the accordions round their group by `first-of-type`, so a div ahead of
        // them keeps the first one square under it.
        <div>
            {stateCount ? (
                <>
                    <div className="border-divider bg-grey-50 text-body1 flex min-h-[38px] items-center justify-between border-x border-t px-4 py-2">
                        <span>
                            <FormattedMessage id="invoices.details.totals" />
                        </span>
                        <span className="font-semibold">
                            {selectedInputScopes.length ? (
                                <MoneyDisplay
                                    amount={[
                                        ...(inProgress ?? []),
                                        ...(completed ?? []),
                                        ...(pending ?? []),
                                        ...(offerWon ?? []),
                                        ...(offerFailed ?? []),
                                    ].reduce(
                                        (acc, curr) => (acc ?? 0) + (curr?.amount ? Number(curr.amount) * -1 : 0),
                                        0
                                    )}
                                    currency={getClassifierText(
                                        classifiers['CURRENCY'],
                                        selectedInputScopes[selectedInputScopes.length - 1].currency
                                    )}
                                />
                            ) : null}
                        </span>
                    </div>
                    {Object.entries(stateCount).map(([key, value], index: number) => {
                        const fetchFilter = handleGetPassiveFilter(key);
                        if (fetchFilter) {
                            return (
                                <MattersAccordionTable
                                    key={`${key}-${value}`}
                                    accordionTitle={
                                        <MatterAmountAccordionTitle
                                            state={key}
                                            count={value}
                                            control={control}
                                            getValues={getValues}
                                            setValue={setValue}
                                            selectedInputScopes={selectedInputScopes}
                                            setSelectedInputScopes={setSelectedInputScopes}
                                        />
                                    }
                                    count={value}
                                    passiveFilter={fetchFilter}
                                    searchText={searchText}
                                    disableUrlState={disableUrlState}
                                    defaultExpanded={index === 0}
                                    defaultFetched={true}
                                    rowsPerPage={rowsPerPage}
                                    columns={columns}
                                    columnConfigId="matter-amount-accordion"
                                    onDataChange={dataChangeHandlers[getGroupName(key)]}
                                />
                            );
                        }
                    })}
                </>
            ) : null}
        </div>
    );
};
